import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearch } from '../hooks/useSearch';
import TicketCard from '../components/TicketCard';
import StatusBadge from '../components/StatusBadge';
import api from '../api/client';
import { getAgentRole, canExport } from '../lib/roles';


const typeOptions = [
  { value: '', label: 'All Types' },
  { value: 'feature_request', label: 'Feature Requests' },
  { value: 'issue', label: 'Issues' },
];

const competitorOptions = [
  { value: '', label: 'All Competitors' },
  { value: 'garmin', label: 'Garmin' },
  { value: 'apple_watch', label: 'Apple Watch' },
  { value: 'polar', label: 'Polar' },
  { value: 'suunto', label: 'Suunto' },
  { value: 'whoop', label: 'Whoop' },
  { value: 'fitbit', label: 'Fitbit' },
  { value: 'wahoo', label: 'Wahoo' },
  { value: 'oura', label: 'Oura' },
  { value: 'hammerhead', label: 'Hammerhead' },
];

const regionOptions = [
  { value: '', label: 'All Regions' },
  { value: 'north_america', label: 'North America' },
  { value: 'europe', label: 'Europe' },
  { value: 'asia_pacific', label: 'Asia Pacific' },
  { value: 'china', label: 'China' },
  { value: 'latin_america', label: 'Latin America' },
  { value: 'other', label: 'Other' },
];

const languageOptions = [
  { value: '', label: 'All Languages' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'it', label: 'Italian' },
];

export function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { results, loading, error, search } = useSearch();

  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [type, setType] = useState(searchParams.get('type') || '');
  const [competitor, setCompetitor] = useState(searchParams.get('competitor') || '');
  const [region, setRegion] = useState(searchParams.get('region') || '');
  const [language, setLanguage] = useState(searchParams.get('language') || '');
  const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [showFilters, setShowFilters] = useState(false);
  const [exportError, setExportError] = useState('');

  // A bare anchor href can't attach the JWT from localStorage, so the export
  // endpoint answers 401 and the browser reports "file wasn't available on
  // site". Go through the client's authenticated blob download instead.
  const handleExportCluster = async (clusterId) => {
    setExportError('');
    try {
      await api.exportClusterEmails(clusterId);
    } catch (err) {
      setExportError(
        err?.status === 403
          ? 'Export needs leadership or admin access.'
          : 'Export failed. Try again, or sign in again if this keeps happening.'
      );
    }
  };

  // Auto-show expanded filters if any advanced filter is set
  useEffect(() => {
    if (region || language || startDate || endDate) setShowFilters(true);
  }, []);

  // Run search on mount and when params change
  useEffect(() => {
    const params = {};
    if (query) params.q = query;
    if (type) params.type = type;
    if (competitor) params.competitor = competitor;
    if (region) params.region = region;
    if (language) params.language = language;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (searchParams.get('cluster_id')) params.cluster_id = searchParams.get('cluster_id');
    params.page = page;
    params.limit = 25;

    search(params);
  }, [query, type, competitor, region, language, startDate, endDate, page, searchParams, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);

    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (type) params.set('type', type);
    if (competitor) params.set('competitor', competitor);
    if (region) params.set('region', region);
    if (language) params.set('language', language);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  const activeFilterCount = [type, competitor, region, language, startDate, endDate].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-coros-text-primary mb-6">Search Feedback</h1>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="mb-8">
        {/* Primary row: query + type + competitor + search button */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tickets and clusters..."
              className="input w-full"
            />
          </div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="input w-full md:w-44">
            {typeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select value={competitor} onChange={(e) => setCompetitor(e.target.value)} className="input w-full md:w-44">
            {competitorOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button type="submit" className="btn-primary">Search</button>
        </div>

        {/* Toggle for advanced filters */}
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            {showFilters ? '▾' : '▸'} More Filters
            {activeFilterCount > 0 && (
              <span style={{
                background: 'var(--coros-red)', color: '#fff',
                fontSize: 10, fontWeight: 700, padding: '1px 6px',
                borderRadius: 10, marginLeft: 2,
              }}>
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Advanced filters row */}
        {showFilters && (
          <div className="flex flex-col md:flex-row gap-3" style={{ marginTop: 8 }}>
            <select value={region} onChange={(e) => setRegion(e.target.value)} className="input w-full md:w-44">
              {regionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="input w-full md:w-44">
              {languageOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div className="flex gap-2 items-center flex-1">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input w-full"
                placeholder="Start date"
              />
              <span style={{ color: 'var(--text-muted)', fontSize: 12, flexShrink: 0 }}>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input w-full"
                placeholder="End date"
              />
            </div>
          </div>
        )}
      </form>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <p className="text-coros-text-muted">Searching...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="text-center py-12">
          <p className="text-coros-status-error">{error}</p>
        </div>
      )}

      {/* No results yet */}
      {!results && !loading && !error && (
        <div className="text-center py-12">
          <p className="text-coros-text-muted">Enter a search query or apply filters to find feedback tickets.</p>
        </div>
      )}

      {/* No search results */}
      {results && !loading && results.clusters?.length === 0 && results.tickets?.length === 0 && (
        <div className="text-center py-12">
          <p className="text-coros-text-muted">
            No results found for <strong>"{query}"</strong> {type || competitor ? '— try a different search' : ''}
          </p>
        </div>
      )}

      {exportError && (
        <div
          className="text-coros-status-error text-sm mb-4"
          role="alert"
          style={{
            padding: '8px 12px',
            border: '1px solid var(--color-error)',
            borderRadius: 4,
          }}
        >
          {exportError}
        </div>
      )}

      {/* Results */}
      {results && !loading && (
        <div>
          {/* Matching clusters */}
          {results.clusters && results.clusters.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-coros-text-primary mb-4">
                Matching Clusters ({results.clusters.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {results.clusters.map((cluster) => {
                  const role = getAgentRole();
                  const mayExport = canExport(role);
                  return (
                    <div
                      key={cluster.id}
                      className="card-hover"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <a
                        href={`/clusters/${cluster.id}`}
                        style={{
                          flex: 1,
                          textDecoration: 'none',
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium text-coros-text-primary">{cluster.name}</h3>
                            <p className="text-coros-text-muted text-sm line-clamp-2 mt-1">
                              {cluster.description}
                            </p>
                          </div>
                          <div className="ml-4 shrink-0 text-right">
                            <span className="text-coros-text-primary font-medium">{cluster.ticket_count}</span>
                            <span className="text-coros-text-muted text-sm ml-1">tickets</span>
                            <div className="mt-1">
                              <StatusBadge status={cluster.status} size="xs" />
                            </div>
                          </div>
                        </div>
                      </a>
                      {mayExport && (
                        <button
                          onClick={() => handleExportCluster(cluster.id)}
                          style={{
                            marginTop: 12,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '6px 10px',
                            fontSize: 11,
                            fontWeight: 600,
                            color: 'var(--text-muted)',
                            background: 'transparent',
                            border: '1px solid var(--border-color)',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'var(--coros-red)';
                            e.currentTarget.style.borderColor = 'var(--coros-red)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--text-muted)';
                            e.currentTarget.style.borderColor = 'var(--border-color)';
                          }}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          Download CSV
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tickets */}
          <div>
            <h2 className="text-lg font-semibold text-coros-text-primary mb-4">
              Tickets ({results.pagination.total.toLocaleString()})
            </h2>

            {results.tickets.length > 0 ? (
              <div className="space-y-4">
                {results.tickets.map((ticket) => (
                  <TicketCard key={ticket.id} ticket={ticket} />
                ))}
              </div>
            ) : (
              <p className="text-coros-text-muted text-center py-8">
                No tickets found matching your search.
              </p>
            )}

            {/* Pagination */}
            {results.pagination.pages > 1 && (
              <div className="flex items-center justify-center space-x-2 mt-8">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-coros-text-muted px-4">
                  Page {page} of {results.pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= results.pagination.pages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Search;
